/**
 * Verstringung (Kapitel H).
 *
 * Im Gespraech knapp beschrieben: „Stringplan heisst quasi eine Linie von Modul
 * zu Modul, in einer Reihenfolge." Genau so ist es hier abgebildet — ein String
 * ist eine geordnete Liste von Modulplaetzen, sonst nichts.
 *
 * Ein Modul wird ueber Flaeche, Reihe und Spalte angesprochen, nicht ueber eine
 * eigene Kennung. Das Raster wird ohnehin bei jeder Anzeige neu gerechnet
 * (siehe raster.ts) — eine gespeicherte Modulkennung waere ein Versprechen, das
 * die Geometrie nicht halten kann. Verschiebt jemand die Flaeche, fallen
 * einzelne Plaetze weg; das wird gemeldet, statt still zu verschwinden.
 */
import { pruefeString } from './elektrik.js';
import { DEFAULT_AUSLEGUNG } from './elektrik.js';
export const modulSchluessel = (zeiger) => `${zeiger.flaecheId}|${zeiger.reihe}:${zeiger.spalte}`;
/** Die Farben, in denen Strings nacheinander vergeben werden (PV-H02). */
export const STRINGFARBEN = [
    '#c0c848',
    '#50b0d8',
    '#d86038',
    '#8c5ad8',
    '#3aa66f',
    '#d8a038',
    '#c84878',
    '#4868c8',
];
export const naechsteFarbe = (anzahlBisher) => STRINGFARBEN[anzahlBisher % STRINGFARBEN.length];
/**
 * Ein Modul an- oder abwaehlen.
 *
 * Zweimal auf dasselbe Modul heisst: wieder heraus. Das ist die einzige
 * Bedienung, die es braucht — Rueckgaengig und Umsortieren ergeben sich daraus.
 */
export function schalteModul(string, zeiger) {
    const schluessel = modulSchluessel(zeiger);
    const vorhanden = string.module.findIndex((m) => modulSchluessel(m) === schluessel);
    if (vorhanden >= 0) {
        return { ...string, module: string.module.filter((_, i) => i !== vorhanden) };
    }
    return { ...string, module: [...string.module, zeiger] };
}
/**
 * Mit dem Finger ueber Module wischen (Wunsch von Mathias, 11/2026).
 *
 * Beginnt das Wischen auf einem Modul, das noch nicht im String ist, kommen
 * alle ueberstrichenen dazu, in der Reihenfolge des Wischens — die ist der Weg
 * des Kabels. Beginnt es auf einem Modul des Strings, werden die
 * ueberstrichenen herausgenommen; so laesst sich ein Fehlgriff mit derselben
 * Bewegung zuruecknehmen.
 *
 * Module eines anderen Strings werden uebersprungen, nicht uebernommen: Wer
 * eine Reihe abfaehrt und am Ende ein Modul zu weit kommt, nimmt sonst dem
 * Nachbarstring eines weg, ohne es zu merken.
 */
export function wische(string, andere, gewischt) {
    const imString = new Set(string.module.map(modulSchluessel));
    const erstes = gewischt[0];
    if (!erstes)
        return { module: string.module, art: 'hinzu', geaendert: 0, uebersprungen: 0 };
    if (imString.has(modulSchluessel(erstes))) {
        const weg = new Set(gewischt.map(modulSchluessel));
        const module = string.module.filter((m) => !weg.has(modulSchluessel(m)));
        return { module, art: 'heraus', geaendert: string.module.length - module.length, uebersprungen: 0 };
    }
    const vergeben = new Set(andere.filter((a) => a.id !== string.id).flatMap((a) => a.module.map(modulSchluessel)));
    const module = [...string.module];
    const gesehen = new Set(imString);
    let uebersprungen = 0;
    for (const zeiger of gewischt) {
        const schluessel = modulSchluessel(zeiger);
        if (gesehen.has(schluessel))
            continue;
        gesehen.add(schluessel);
        if (vergeben.has(schluessel)) {
            uebersprungen++;
            continue;
        }
        module.push(zeiger);
    }
    return { module, art: 'hinzu', geaendert: module.length - string.module.length, uebersprungen };
}
/**
 * Die ganze Anlage durchrechnen.
 *
 * Alles hier sind Warnungen, keine Verbote. Ein String ueber Flaechengrenzen
 * hinweg ist ertragsseitig unguenstig, wird aber gemacht (PV-C02) — das
 * Programm sagt es und laesst es zu.
 */
export function pruefeAnlage(eingabe) {
    const auslegung = eingabe.auslegung ?? DEFAULT_AUSLEGUNG;
    const befunde = new Map();
    const warnungen = [];
    const weggefallen = [];
    const verstringt = new Set();
    /** Strings, die parallel an einem Tracker hängen: ihre Ströme addieren sich. */
    const stromJeTracker = new Map();
    for (const string of eingabe.strings) {
        // Zeiger aussortieren, zu denen es kein Modul mehr gibt.
        const gueltig = [];
        for (const zeiger of string.module) {
            const plaetze = eingabe.vorhandeneModule.get(zeiger.flaecheId);
            if (plaetze?.has(`${zeiger.reihe}:${zeiger.spalte}`)) {
                gueltig.push(zeiger);
                verstringt.add(modulSchluessel(zeiger));
            }
            else {
                weggefallen.push(zeiger);
            }
        }
        if (gueltig.length === 0)
            continue;
        // Ein String darf Module mehrerer Flaechen enthalten (PV-C02) — aber wenn
        // die unterschiedlich stehen, kostet das Ertrag.
        const azimute = new Set(gueltig.map((z) => Math.round(eingabe.azimutJeFlaeche.get(z.flaecheId) ?? 0)));
        if (azimute.size > 1) {
            warnungen.push({
                art: 'gemischte_ausrichtung',
                stringId: string.id,
                text: `${string.name}: Module mit ${azimute.size} verschiedenen Ausrichtungen in einem String. ` +
                    'Zulässig, kostet aber Ertrag.',
            });
        }
        const modultypen = new Set(gueltig.map((z) => JSON.stringify(eingabe.modulJeFlaeche.get(z.flaecheId))));
        if (modultypen.size > 1) {
            warnungen.push({
                art: 'mehrere_module_je_string',
                stringId: string.id,
                text: `${string.name}: verschiedene Modultypen in einem String. Die Spannungsprüfung rechnet mit dem ersten.`,
            });
        }
        const modul = eingabe.modulJeFlaeche.get(gueltig[0].flaecheId);
        if (!modul || !eingabe.wechselrichter)
            continue;
        const befund = pruefeString(gueltig.length, modul, {
            maxEingangsspannungV: eingabe.wechselrichter.maxEingangsspannungV,
            ...(eingabe.wechselrichter.mppUntergrenzeV !== undefined
                ? { mppUntergrenzeV: eingabe.wechselrichter.mppUntergrenzeV }
                : {}),
        }, auslegung);
        befunde.set(string.id, befund);
        // Mindesteingangsspannung: im Sommer im MPP. Darunter läuft der
        // Wechselrichter gar nicht erst an.
        const wrDaten = eingabe.wechselrichter;
        if (wrDaten.minEingangsspannungV !== undefined &&
            befund.spannungWarmMppV !== undefined &&
            befund.spannungWarmMppV < wrDaten.minEingangsspannungV) {
            warnungen.push({
                art: 'unter_mindestspannung',
                stringId: string.id,
                text: `${string.name}: ${gueltig.length} Module liefern im Sommer ${befund.spannungWarmMppV.toFixed(0)} V — ` +
                    `der Wechselrichter braucht mindestens ${wrDaten.minEingangsspannungV} V.`,
            });
        }
        if (modul.maxSystemspannungV !== undefined && befund.spannungKaltV > modul.maxSystemspannungV) {
            warnungen.push({
                art: 'ueber_systemspannung',
                stringId: string.id,
                text: `${string.name}: ${befund.spannungKaltV.toFixed(0)} V bei Kälte — ` +
                    `die Module sind nur bis ${modul.maxSystemspannungV} V Systemspannung zugelassen.`,
            });
        }
        if (modul.imppA !== undefined && string.mppt !== undefined) {
            const liste = stromJeTracker.get(string.mppt) ?? [];
            liste.push({ name: string.name, strom: modul.imppA });
            stromJeTracker.set(string.mppt, liste);
        }
        if (befund.befund === 'zu_lang') {
            warnungen.push({ art: 'string_zu_lang', stringId: string.id, text: `${string.name}: ${befund.meldung}` });
        }
        else if (befund.befund === 'zu_kurz') {
            warnungen.push({ art: 'string_zu_kurz', stringId: string.id, text: `${string.name}: ${befund.meldung}` });
        }
    }
    // Module, die in keinem String liegen — damit beim Abschluss keines vergessen
    // wird (PV-H02).
    const ohneString = [];
    let moduleGesamt = 0;
    for (const [flaecheId, plaetze] of eingabe.vorhandeneModule) {
        for (const platz of plaetze) {
            moduleGesamt++;
            const [reihe, spalte] = platz.split(':').map(Number);
            const zeiger = { flaecheId, reihe: reihe ?? 0, spalte: spalte ?? 0 };
            if (!verstringt.has(modulSchluessel(zeiger)))
                ohneString.push(zeiger);
        }
    }
    if (ohneString.length > 0) {
        warnungen.push({
            art: 'modul_ohne_string',
            text: `${ohneString.length} Modul(e) liegen in keinem String.`,
        });
    }
    if (weggefallen.length > 0) {
        warnungen.push({
            art: 'modul_weggefallen',
            text: `${weggefallen.length} Modul(e) aus der Verstringung gibt es nicht mehr — ` +
                'die Fläche wurde geändert, nachdem verstringt wurde.',
        });
    }
    // Ein String an einem Tracker, den das Gerät nicht hat, fiel bisher still
    // durch die Stromprüfung.
    const trackerZahl = eingabe.wechselrichter?.anzahlMppt;
    if (trackerZahl !== undefined) {
        const daneben = eingabe.strings.filter((s) => (s.mppt ?? 1) > trackerZahl);
        if (daneben.length > 0) {
            warnungen.push({
                art: 'mppt_fehlt',
                text: `${daneben.map((s) => s.name).join(', ')} ${daneben.length === 1 ? 'hängt' : 'hängen'} an einem Tracker, ` +
                    `den der Wechselrichter nicht hat (${trackerZahl} Tracker).`,
            });
        }
    }
    // Strom je Tracker: Strings an einem Tracker liegen parallel.
    const grenzen = eingabe.wechselrichter?.maxStromJeMpptA;
    if (grenzen) {
        for (const [tracker, liste] of [...stromJeTracker].sort((a, b) => a[0] - b[0])) {
            const grenze = grenzen[tracker - 1];
            if (grenze === undefined)
                continue;
            const summe = liste.reduce((s, x) => s + x.strom, 0);
            if (summe > grenze + 1e-9) {
                warnungen.push({
                    art: 'strom_mppt',
                    text: `Tracker ${tracker}: ${liste.map((x) => x.name).join(' und ')} ergeben zusammen ` +
                        `${summe.toLocaleString('de-AT', { maximumFractionDigits: 1 })} A — ` +
                        `der Tracker verträgt ${grenze.toLocaleString('de-AT', { maximumFractionDigits: 1 })} A.`,
                });
            }
        }
    }
    const maxPv = eingabe.wechselrichter?.maxPvLeistungKw;
    if (maxPv !== undefined && eingabe.leistungKwp !== undefined && eingabe.leistungKwp > maxPv + 1e-9) {
        warnungen.push({
            art: 'pv_leistung_zu_hoch',
            text: `${eingabe.leistungKwp.toLocaleString('de-AT', { maximumFractionDigits: 2 })} kWp an Modulen — ` +
                `der Wechselrichter nimmt höchstens ${maxPv.toLocaleString('de-AT', { maximumFractionDigits: 1 })} kW PV-Leistung.`,
        });
    }
    if (!eingabe.wechselrichter && eingabe.strings.length > 0) {
        warnungen.push({
            art: 'kein_wechselrichter',
            text: 'Ohne Wechselrichter kann die Stringspannung nicht geprüft werden.',
        });
    }
    return {
        befunde,
        warnungen,
        ohneString,
        weggefallen,
        moduleGesamt,
        moduleVerstringt: verstringt.size,
    };
}
/**
 * Modulleistung zu Wechselrichterleistung (PV-I02).
 *
 * Ueberbelegung ist gewollt — die Anlage erreicht ihre Spitzenleistung ohnehin
 * selten — aber nicht unbegrenzt.
 */
export function belegungsverhaeltnis(modulleistungKwp, acLeistungKw) {
    if (acLeistungKw <= 0)
        return { verhaeltnis: 0 };
    const verhaeltnis = modulleistungKwp / acLeistungKw;
    if (verhaeltnis > 1.5) {
        return {
            verhaeltnis,
            hinweis: `Deutliche Überbelegung (${verhaeltnis.toFixed(2)}). Üblich sind 1,0 bis 1,3.`,
        };
    }
    if (verhaeltnis < 0.8) {
        return {
            verhaeltnis,
            hinweis: `Der Wechselrichter ist reichlich groß (${verhaeltnis.toFixed(2)}). Üblich sind 1,0 bis 1,3.`,
        };
    }
    return { verhaeltnis };
}
/**
 * Vorschlag zur Verstringung: Module einer Flaeche im Maeander, gleichmaessig
 * auf die Tracker verteilt (PV-H07, in der Spec als spaeter eingestuft).
 *
 * Steht hier, weil es aus dem Vorhandenen faellt und dem Planer die stumpfe
 * Arbeit abnimmt. Der Nutzer korrigiert danach, statt bei null zu beginnen.
 *
 * **Maeander, nicht zeilenweise.** Frueher lief jede Reihe links nach rechts,
 * und zwischen zwei Reihen sprang das Kabel quer ueber das ganze Dach zurueck
 * — im Stringplan als lange Diagonale zu sehen. Das kostet zweierlei:
 *
 *   Leitung. Der Ruecksprung ist bei jeder Reihe so lang wie die Reihe selbst.
 *   Bei sieben Modulen nebeneinander sind das rund acht Meter je Reihenwechsel,
 *   und Solarleitung liegt nicht gratis am Dach.
 *
 *   Und, schwerer wiegend: die aufgespannte Flaeche. Hin- und Rueckleitung
 *   eines Strings bilden eine Schleife; je groesser die von ihr umschlossene
 *   Flaeche, desto hoeher die Spannung, die ein Blitzschlag in der Naehe darin
 *   induziert. OVE/OENORM E 8001-4-712 verlangt ausdruecklich, diese Flaeche
 *   klein zu halten. Die Diagonale quer ueber das Dach ist genau das Gegenteil.
 *
 * Im Maeander laeuft die erste Reihe hin, die zweite zurueck, die dritte wieder
 * hin. Bei gerader Reihenzahl endet der String auf derselben Seite, auf der er
 * begonnen hat — dann ist auch der Weg zum Wechselrichter kurz.
 */
export function schlageStringsVor(vorhandeneModule, maxModuleJeString, anzahlMppt = 2) {
    const strings = [];
    // Passt nicht einmal ein Modul unter die Eingangsspannung, gibt es keinen
    // Vorschlag — mit 0 je String liefe die Schleife unten endlos.
    if (!(maxModuleJeString >= 1))
        return strings;
    for (const [flaecheId, plaetze] of vorhandeneModule) {
        // Reihenweise, innerhalb der Reihe abwechselnd hin und zurueck — so laeuft
        // das Kabel auch, ohne Ruecksprung quer ueber das Dach.
        const zeiger = [...plaetze].map((platz) => {
            const [reihe, spalte] = platz.split(':').map(Number);
            return { flaecheId, reihe: reihe ?? 0, spalte: spalte ?? 0 };
        });
        // Welche Reihen gibt es? Nicht jede Reihe ist belegt, und die Richtung
        // haengt an der Position in der Reihenfolge, nicht am Reihenindex — sonst
        // dreht eine ausgelassene Reihe die Laufrichtung der uebernaechsten um.
        const reihen = [...new Set(zeiger.map((z) => z.reihe))].sort((a, b) => a - b);
        const sortiert = reihen.flatMap((reihe, lauf) => {
            const dieser = zeiger
                .filter((z) => z.reihe === reihe)
                .sort((a, b) => a.spalte - b.spalte);
            return lauf % 2 === 0 ? dieser : dieser.reverse();
        });
        // Gleichmäßig teilen: 25 Module bei höchstens 12 je String sind 9, 8 und
        // 8 — nicht 12, 12 und ein einzelnes, das unter den MPP-Bereich fällt.
        const anzahl = Math.ceil(sortiert.length / maxModuleJeString);
        const grund = Math.floor(sortiert.length / anzahl);
        const mehr = sortiert.length % anzahl;
        for (let k = 0, i = 0; k < anzahl; k++) {
            const laenge = grund + (k < mehr ? 1 : 0);
            const teil = sortiert.slice(i, i + laenge);
            i += laenge;
            strings.push({
                id: `vorschlag-${strings.length + 1}`,
                name: `String ${strings.length + 1}`,
                farbe: naechsteFarbe(strings.length),
                module: teil,
                mppt: (strings.length % anzahlMppt) + 1,
            });
        }
    }
    return strings;
}
//# sourceMappingURL=verstringung.js.map